import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { RefreshCw, Activity, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';

const PCT = (n) => `${Math.round((Number(n || 0)) * 100)}%`;
const BAND_CLASS = {
  GREEN: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  YELLOW: 'bg-amber-100 text-amber-800 border-amber-300',
  RED: 'bg-red-100 text-red-800 border-red-300',
};
const TREND_ICON = {
  IMPROVED: { label: 'Improving', cls: 'text-emerald-700' },
  STABLE: { label: 'Stable', cls: 'text-zinc-600' },
  DECLINING: { label: 'Declining', cls: 'text-red-700' },
  NEW: { label: 'New', cls: 'text-sky-700' },
};

export function MappingHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, carrier: null });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await compensationAPI.mappingHealth({ limit_per_carrier: 3 });
      setData(r.data);
    } catch {
      toast.error('Failed to load mapping health');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const clone = async (tmplId) => {
    try {
      const r = await compensationAPI.cloneImportTemplate(tmplId);
      toast.success(`Cloned to ${r.data.name} (${r.data.version})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Clone failed'); }
  };

  const activate = async (tmplId) => {
    try {
      await compensationAPI.activateImportTemplate(tmplId);
      toast.success('Template activated');
      load();
      setDrawer((d) => ({ ...d, open: false }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Activate failed'); }
  };

  if (!data || data.carriers.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" />Mapping Health</CardTitle>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="mapping-health-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="text-sm text-zinc-500 italic">No imports yet — Mapping Health will appear after the first carrier statement is imported.</CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="mapping-health-panel">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" />Mapping Health</CardTitle>
            <CardDescription>Last 3 imports per carrier · sudden issue spikes flag possible format changes.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="mapping-health-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.carriers.map((c) => {
            const latest = c.latest || {};
            const band = latest.status_band || 'GREEN';
            const trend = TREND_ICON[c.trend] || TREND_ICON.NEW;
            return (
              <button key={c.carrier_id} type="button"
                      onClick={() => setDrawer({ open: true, carrier: c })}
                      data-testid={`mapping-health-card-${c.carrier_id}`}
                      className={`text-left rounded-lg border-2 p-4 hover:shadow-md transition ${BAND_CLASS[band]} ${c.format_change_detected ? 'ring-2 ring-red-500' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{c.carrier_id}</div>
                    {latest.template_name && (
                      <div className="text-xs opacity-75 truncate">{latest.template_name} · {latest.template_version}</div>
                    )}
                  </div>
                  <Badge variant="outline" className="bg-white/60 shrink-0">{band}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div><div className="opacity-75">Records</div><div className="font-semibold">{latest.total_rows}</div></div>
                  <div><div className="opacity-75">Issues</div><div className="font-semibold">{latest.issue_count}</div></div>
                  <div><div className="opacity-75">Issue %</div><div className="font-semibold">{PCT(latest.issue_rate)}</div></div>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className={`font-medium ${trend.cls}`}>{trend.label}</span>
                  <span className="opacity-75">{latest.completed_at ? new Date(latest.completed_at).toLocaleDateString() : '—'}</span>
                </div>
                {c.format_change_detected && (
                  <div className="mt-3 rounded bg-red-100 px-2 py-1.5 text-xs text-red-900 font-medium flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Possible format change — review mapping
                  </div>
                )}
              </button>
            );
          })}
        </CardContent>
      </Card>

      <Dialog open={drawer.open} onOpenChange={(v) => !v && setDrawer({ open: false, carrier: null })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Mapping Health: {drawer.carrier?.carrier_id}</DialogTitle>
            <DialogDescription>
              Trend: <span className={TREND_ICON[drawer.carrier?.trend || 'NEW'].cls}>{TREND_ICON[drawer.carrier?.trend || 'NEW'].label}</span>
              {drawer.carrier?.format_change_detected && (
                <span className="ml-3 text-red-700 font-medium">⚠ Possible format change detected</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {drawer.carrier && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-zinc-500">Last 3 imports</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead className="text-right">Records</TableHead>
                      <TableHead className="text-right">Imported</TableHead>
                      <TableHead className="text-right">Review</TableHead>
                      <TableHead className="text-right">Dups</TableHead>
                      <TableHead className="text-right">Errors</TableHead>
                      <TableHead className="text-right">Issue %</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drawer.carrier.imports.map((imp) => (
                      <TableRow key={imp.statement_id} data-testid={`mapping-health-import-${imp.statement_id}`}>
                        <TableCell className="text-sm">{imp.completed_at ? new Date(imp.completed_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-sm">{imp.template_name || '—'}{imp.template_version ? ` (${imp.template_version})` : ''}</TableCell>
                        <TableCell className="text-right">{imp.total_rows}</TableCell>
                        <TableCell className="text-right">{imp.imported}</TableCell>
                        <TableCell className="text-right">{imp.needs_review}</TableCell>
                        <TableCell className="text-right">{imp.duplicates_flagged}</TableCell>
                        <TableCell className="text-right">{imp.error_count}</TableCell>
                        <TableCell className="text-right font-medium">{PCT(imp.issue_rate)}</TableCell>
                        <TableCell><Badge className={BAND_CLASS[imp.status_band]}>{imp.status_band}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="rounded border p-3 bg-zinc-50 space-y-3">
                <div className="text-sm font-medium">Template Actions</div>
                {drawer.carrier.active_template ? (
                  <div className="text-sm text-zinc-700">
                    Active: <b>{drawer.carrier.active_template.name}</b> ({drawer.carrier.active_template.version})
                  </div>
                ) : drawer.carrier.imports[0]?.template_id ? (
                  <div className="text-sm text-zinc-700">
                    Latest used: <b>{drawer.carrier.imports[0].template_name}</b> ({drawer.carrier.imports[0].template_version})
                  </div>
                ) : (
                  <div className="text-sm text-zinc-500 italic">No template lineage on these imports.</div>
                )}
                <div className="flex flex-wrap gap-2">
                  {drawer.carrier.imports[0]?.template_id && (
                    <Button size="sm" variant="outline" onClick={() => clone(drawer.carrier.imports[0].template_id)} data-testid="mapping-health-clone-btn">
                      Clone latest as new version
                    </Button>
                  )}
                  {drawer.carrier.imports[0]?.template_id && !drawer.carrier.active_template?.template_id && (
                    <Button size="sm" onClick={() => activate(drawer.carrier.imports[0].template_id)} data-testid="mapping-health-activate-btn">
                      Set latest as Active version
                    </Button>
                  )}
                </div>
                <p className="text-xs text-zinc-500">Use &quot;Create New Template Version&quot; by uploading a corrected statement on the Import tab with &quot;Save this mapping as a reusable template&quot; enabled.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrawer({ open: false, carrier: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
